import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { dashboardAPI } from '../services/api';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const Dashboard = () => {
    const { user } = useAuth();
    const { t } = useLanguage();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const fetchRef = useRef(false);

    useEffect(() => {
        if (!fetchRef.current) {
            fetchRef.current = true;
            fetchStats();
        }
    }, []);

    const fetchStats = async () => {
        try {
            const response = await dashboardAPI.stats();
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-lg">{t('common.loading')}</div>
            </div>
        );
    }

    const chartData = {
        labels: stats?.monthly_trend?.map((item) => {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return months[item.month - 1];
        }) || [],
        datasets: [
            {
                label: 'Certificates',
                data: stats?.monthly_trend?.map((item) => item.count) || [],
                backgroundColor: 'rgba(59, 130, 246, 0.5)',
                borderColor: 'rgba(59, 130, 246, 1)',
                borderWidth: 1,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Monthly Certificate Trend',
            },
        },
    };

    return (
        <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    {t('dashboard.welcome')}, {user?.name}!
                </h1>
                <p className="text-gray-600 mt-1">
                    {user?.role === 'admin' ? 'Administrator' : 'Staff Member'}
                </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white shadow rounded-lg p-6">
                    <div className="text-sm font-medium text-gray-500">
                        {t('dashboard.totalCertificates')}
                    </div>
                    <div className="text-3xl font-bold text-gray-800 mt-2">
                        {stats?.total_certificates || 0}
                    </div>
                </div>

                <div className="bg-white shadow rounded-lg p-6">
                    <div className="text-sm font-medium text-gray-500">
                        {t('dashboard.activeCertificates')}
                    </div>
                    <div className="text-3xl font-bold text-green-600 mt-2">
                        {stats?.active_certificates || 0}
                    </div>
                </div>

                <div className="bg-white shadow rounded-lg p-6">
                    <div className="text-sm font-medium text-gray-500">
                        {t('dashboard.certificatesThisMonth')}
                    </div>
                    <div className="text-3xl font-bold text-blue-600 mt-2">
                        {stats?.this_month || stats?.certificates_this_month || 0}
                    </div>
                </div>

                <div className="bg-white shadow rounded-lg p-6">
                    <div className="text-sm font-medium text-gray-500">
                        {t('dashboard.certificatesToday')}
                    </div>
                    <div className="text-3xl font-bold text-purple-600 mt-2">
                        {stats?.today || stats?.certificates_today || 0}
                    </div>
                </div>
            </div>

            {/* Cancelled Certificates */}
            {stats?.cancelled_certificates !== undefined && (
                <div className="bg-white shadow rounded-lg p-6">
                    <div className="text-sm font-medium text-gray-500">
                        {t('dashboard.cancelledCertificates') || 'Cancelled Certificates'}
                    </div>
                    <div className="text-3xl font-bold text-red-600 mt-2">
                        {stats?.cancelled_certificates || 0}
                    </div>
                </div>
            )}

            {/* Admin Only Stats */}
            {user?.role === 'admin' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white shadow rounded-lg p-6">
                        <div className="text-sm font-medium text-gray-500">
                            {t('dashboard.totalStaff')}
                        </div>
                        <div className="text-3xl font-bold text-gray-800 mt-2">
                            {stats?.total_staff || 0}
                        </div>
                    </div>

                    <div className="bg-white shadow rounded-lg p-6">
                        <div className="text-sm font-medium text-gray-500">
                            {t('dashboard.activeStaff')}
                        </div>
                        <div className="text-3xl font-bold text-green-600 mt-2">
                            {stats?.active_staff || 0}
                        </div>
                    </div>
                </div>
            )}

            {/* Chart — visible to all users */}
            {stats?.monthly_trend?.length > 0 && (
                <div className="bg-white shadow rounded-lg p-6">
                    <Bar data={chartData} options={chartOptions} />
                </div>
            )}

            {/* Recent Activity */}
            <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                    {t('dashboard.recentActivity')}
                </h2>
                <div className="space-y-3">
                    {(stats?.recent_activity || stats?.recent_activities)?.length > 0 ? (
                        [...new Map(
                            (stats?.recent_activity || stats?.recent_activities).map(a => [a.id, a])
                        ).values()].map((activity) => (
                            <div
                                key={activity.id}
                                className="flex items-center justify-between border-b pb-2"
                            >
                                <div>
                                    <div className="font-medium">{activity.action}</div>
                                    <div className="text-sm text-gray-500">
                                        {activity.description}
                                    </div>
                                </div>
                                <div className="text-sm text-gray-400">
                                    {activity.user_name || activity.user?.name}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-gray-500 text-center py-4">
                            {t('common.noData')}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;